"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Diamond, CheckCircle, X } from "lucide-react"; // Added icons
import { useAuth } from "./contexts/AuthContext";
import { toast } from "react-toastify"; // Import toast

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password); // Attempt login
      toast.success("Successfully logged in!", {
        position: "top-right",
        autoClose: 3000,
        icon: <CheckCircle size={20} className="text-emerald-600" />,
      });
    
      navigate("/dashboard"); 
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to log in. Please try again.";
      toast.error(errorMessage, {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50 p-4">
      {/* Enhanced background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-emerald-100 rounded-full opacity-10 blur-3xl transform translate-x-1/4 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-emerald-200 rounded-full opacity-10 blur-3xl transform -translate-x-1/4 translate-y-1/4"></div>
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-emerald-300 rounded-full opacity-5 blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-emerald-100 rounded-full opacity-10 blur-3xl"></div>

        {/* Decorative gemstone-inspired elements */}
        <svg
          className="absolute top-20 right-20 w-32 h-32 text-emerald-200 opacity-20"
          viewBox="0 0 100 100"
          fill="currentColor"
        >
          <polygon points="50,0 100,25 100,75 50,100 0,75 0,25" />
        </svg>
        <svg
          className="absolute bottom-20 left-20 w-24 h-24 text-emerald-300 opacity-10"
          viewBox="0 0 100 100"
          fill="currentColor"
        >
          <polygon points="50,0 95,25 95,75 50,100 5,75 5,25" />
        </svg>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-white/90 backdrop-blur-sm rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] overflow-hidden border border-emerald-50"
      >
        <div className="p-10">
          <div className="flex flex-col items-center mb-10">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 rounded-full mb-6 shadow-lg shadow-emerald-200"
            >
              <Diamond className="h-10 w-10 text-white" />
            </motion.div>
            <motion.h1
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-3xl font-bold text-gray-800 tracking-tight"
            >
              Welcome Back
            </motion.h1>
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-gray-500 mt-2 text-center font-light"
            >
              Log in to view your daily reports
            </motion.p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-6">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="relative group"
              >
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="peer w-full border-b-2 border-gray-300 py-3 px-1 text-gray-900 placeholder-transparent focus:border-emerald-500 focus:outline-none bg-transparent"
                  placeholder="Email"
                  required
                />
                <label
                  htmlFor="email"
                  className="absolute left-1 -top-5 text-sm text-gray-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-5 peer-focus:text-sm peer-focus:text-emerald-600"
                >
                  Email
                </label>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-emerald-500 transition-all duration-300 group-hover:w-full"></div>
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="relative group"
              >
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="peer w-full border-b-2 border-gray-300 py-3 px-1 text-gray-900 placeholder-transparent focus:border-emerald-500 focus:outline-none bg-transparent"
                  placeholder="Password"
                  required
                />
                <label
                  htmlFor="password"
                  className="absolute left-1 -top-5 text-sm text-gray-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-5 peer-focus:text-sm peer-focus:text-emerald-600"
                >
                  Password
                </label>
                <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-emerald-500 transition-all duration-300 group-hover:w-full"></div>
              </motion.div>
            </div>

            <div className="flex justify-end">
              <motion.div initial={{ x: 10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
                <Link
                  to="/forgot-password" // Changed href to to for react-router-dom
                  className="text-sm font-medium text-emerald-600 hover:text-emerald-500 transition-colors"
                >
                  Forgot password?
                </Link>
              </motion.div>
            </div>

            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              whileHover={{ scale: 1.02, boxShadow: "0 10px 25px -5px rgba(16, 185, 129, 0.2)" }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-medium rounded-xl shadow-lg shadow-emerald-200/50 transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 hover:shadow-xl"
            >
              Sign in
            </motion.button>
          </form>
        </div>

        <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600"></div>
      </motion.div>
    </div>
  );
}